import { useCallback, useEffect, useState } from "react";
import { getMatch, getBullseyeMatch, getMatchRegistrationSummary, getBullseyeMatchRegistrationSummary, getMyMatchRegistration, getMyBullseyeMatchRegistration, getRelatedMatches } from "../../lib/leaderboardApi";
import { getCompetitionAuthProfile } from "../../lib/competitionAuthProfile";
import { hasRegistrationPolicy } from "./registrationPolicy";
import useRegistrationClock from "./useRegistrationClock";

const readers = {
  arc: { match: getMatch, summary: getMatchRegistrationSummary, mine: getMyMatchRegistration },
  bullseye: { match: getBullseyeMatch, summary: getBullseyeMatchRegistrationSummary, mine: getMyBullseyeMatchRegistration },
};

export default function useRegistrationPage(family, matchId) {
  const [state, setState] = useState({ loading: true, error: "", match: null, summary: null, profile: null, registration: null });
  const [revision, setRevision] = useState(0);
  const [summaryWarning, setSummaryWarning] = useState("");
  useRegistrationClock(Boolean(state.match));
  useEffect(() => {
    let active = true;
    setState({ loading: true, error: "", match: null, summary: null, profile: null, registration: null });
    setSummaryWarning("");
    async function load() {
      try {
        const api = readers[family];
        const [match, summary, profile, related] = await Promise.all([api.match(matchId), api.summary(matchId), getCompetitionAuthProfile(), getRelatedMatches(matchId, family)]);
        // A failed personal-registration read must not be treated as a new registration.
        const registration = profile?.userId ? await api.mine(matchId, profile.userId) : null;
        if (!match || !summary) throw new Error("Match registration information is unavailable. Please try again.");
        if (active) setState({ loading: false, error: "", match, summary, profile, registration, related });
      } catch (error) {
        if (active) setState(current => ({ ...current, loading: false, error: error?.message || "Please try again." }));
      }
    }
    load();
    return () => { active = false; };
  }, [family, matchId, revision]);
  const setRegistration = useCallback(registration => setState(current => ({ ...current, registration })), []);
  const refreshSummary = useCallback(async () => {
    try {
      const summary = await readers[family].summary(matchId);
      if (!summary) throw new Error("Missing summary");
      setState(current => ({ ...current, summary }));
      setSummaryWarning("");
    } catch {
      // Keep the accepted receipt even if a secondary read fails. Never prompt a second submission.
      setSummaryWarning("Your registration was saved. Availability could not refresh; try refreshing availability before making another change.");
    }
  }, [family, matchId]);
  const policyEnabled = hasRegistrationPolicy(state.summary);
  useEffect(() => {
    if (!policyEnabled) return undefined;
    let active = true, reading = false;
    const refresh = async () => {
      if (document.hidden || reading) return;
      reading = true;
      try {
        const [match, summary] = await Promise.all([readers[family].match(matchId), readers[family].summary(matchId)]);
        if (active && match && summary) setState(current => ({ ...current, match, summary }));
      } catch { /* The local deadline clock remains active during failed reads. */ }
      finally { reading = false; }
    };
    const timer = setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { active = false; clearInterval(timer); window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, [policyEnabled, family, matchId]);
  return { ...state, summaryWarning, setRegistration, refreshSummary, retry: () => setRevision(value => value + 1) };
}
